/**
 * PlanComparison — theme-agnostic, chrome-free pricing content (header + billing
 * toggle + tier card grid). Consumed by both the standalone /pricing page and an
 * in-app overlay; the caller injects CTA behavior via `onSelectPlan(tier, interval)`.
 *
 * Layout: centered header (eyebrow → serif title → subtitle → billing segment) →
 * 3-up card grid (featured "standard" card lifted + accented).
 *
 * Flow:
 *  - Plans are fetched via useBillingPlans and grouped per tier (groupPlansByTier);
 *    loading shows a spinner, errors show a friendly message.
 *  - `period` state drives displayed price, save tag, and per-card billing note.
 *  - The segment "thumb" is positioned by measuring the active button's physical
 *    offsetLeft/offsetWidth — direction-agnostic, so RTL works without sign flips.
 *    Re-measured on period/language change (useLayoutEffect) and after web-font
 *    swap / reflow (ResizeObserver).
 *  - Prices are formatted via formatPlanPrice (Intl currency, locale-aware).
 *  - CTA delegates to the caller: each card's button calls
 *    `onSelectPlan(tier, period)` — no auth/checkout/navigate logic lives here.
 *
 * Intentionally excludes page chrome: no min-h-screen wrapper, no language toggle,
 * no copyright footer, no theme lock — the host (page or overlay) owns those.
 *
 * @layer-2 — domain composition
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheckBig, Star } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";
import { ThemedButton, ThemedAnchorButton, PageSpinner } from "@/shared/components";
import { useBillingPlans } from "../hooks/useBillingPlans";
import { groupPlansByTier, formatPlanPrice, annualSavingPercent, type TierGroup } from "../lib/billingHelpers";
import type { BillingInterval, ServiceTier } from "../types";

/** Static contact address for the Enterprise "Custom quote" tier. */
const ENTERPRISE_CONTACT = "mailto:admin@ur-nammu.com";

/** standard is the featured/"popular" tier; it gets the primary CTA. */
const FEATURED_TIER: ServiceTier = "standard";
const CARD_DELAYS = ["0.14s", "0.20s", "0.26s"] as const;

interface PlanComparisonProps {
  /** Called when a plan CTA is clicked, with the chosen tier + active interval. */
  onSelectPlan: (tier: ServiceTier, interval: BillingInterval) => void;
  /** When true, all CTAs are disabled (e.g. checkout in flight). */
  pending?: boolean;
  /** Optional heading override (e.g. first-time vs resubscribe copy). Defaults to t("title"). */
  heading?: string;
}

export function PlanComparison({ onSelectPlan, pending = false, heading }: PlanComparisonProps) {
  const { t } = useTranslation("pricing");
  const { lang, numLocale } = useLocale();
  const [period, setPeriod] = useState<BillingInterval>("monthly");

  const { data: plans, isLoading, isError } = useBillingPlans();
  const groups = groupPlansByTier(plans ?? []);

  const segRef = useRef<HTMLDivElement>(null);
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const yearlyRef = useRef<HTMLButtonElement>(null);
  const [thumb, setThumb] = useState({ start: 0, width: 0 });

  /**
   * Measure the active button and move the thumb to cover it.
   * `start` is a direction-aware inline-start distance (RTL: from the right
   * edge) so the thumb is positioned purely via the logical `inset-inline-start`
   * — no physical transform, correct in both LTR and RTL.
   */
  const positionThumb = useCallback(() => {
    const active = period === "monthly" ? monthlyRef.current : yearlyRef.current;
    const seg = segRef.current;
    if (!active || !seg) return;
    const segRect = seg.getBoundingClientRect();
    const btnRect = active.getBoundingClientRect();
    const rtl = getComputedStyle(seg).direction === "rtl";
    const start = rtl ? segRect.right - btnRect.right : btnRect.left - segRect.left;
    setThumb({ start, width: active.offsetWidth });
  }, [period]);

  // Position before paint on period or language change (Arabic labels differ in width).
  useLayoutEffect(() => {
    positionThumb();
  }, [positionThumb, lang]);

  // Re-measure after web-font swap / any reflow of the segment.
  useEffect(() => {
    const seg = segRef.current;
    if (!seg) return;
    const ro = new ResizeObserver(() => positionThumb());
    ro.observe(seg);
    return () => ro.disconnect();
  }, [positionThumb]);

  return (
    <div className="mx-auto" style={{ maxWidth: 1400 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header
        className="text-center mx-auto animate-fadeUp"
        style={{ maxWidth: 680, marginBottom: "clamp(40px, 5vh, 60px)" }}
      >
        <div
          className="inline-flex items-center gap-2 uppercase"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            color: "var(--gold-text)",
            marginBottom: 20,
          }}
        >
          <span className="u-ornament-dot" style={{ width: 5, height: 5 }} />
          {t("eyebrow")}
        </div>

        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(2.2rem, 5vw, 3.5rem)",
            lineHeight: 1.06,
            letterSpacing: "-0.02em",
            color: "var(--text-primary)",
          }}
        >
          {heading ?? t("title")}
        </h1>

        <p
          className="text-pretty"
          style={{
            marginTop: 18,
            fontSize: "clamp(15px, 1.6vw, 17px)",
            lineHeight: 1.6,
            color: "var(--text-secondary)",
          }}
        >
          {t("subtitle")}
        </p>

        {/* ── Billing toggle ──────────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-3" style={{ marginTop: 34 }}>
          <div
            ref={segRef}
            role="tablist"
            aria-label={t("billing.periodLabel")}
            className="relative inline-flex"
            style={{
              padding: 4,
              background: "var(--surface-low)",
              border: "1px solid var(--border-light)",
              borderRadius: "var(--radius-pill)",
            }}
          >
            {/* Sliding thumb — positioned by measuring the active button. */}
            <span
              aria-hidden="true"
              className="absolute z-0"
              style={{
                top: 4,
                insetInlineStart: thumb.start,
                height: "calc(100% - 8px)",
                width: thumb.width,
                background: "var(--surface-card)",
                borderRadius: "var(--radius-pill)",
                boxShadow: "var(--shadow-card)",
                transition:
                  "inset-inline-start .32s var(--ease-out-expo), width .32s var(--ease-out-expo)",
              }}
            />
            <SegButton
              ref={monthlyRef}
              active={period === "monthly"}
              onClick={() => setPeriod("monthly")}
              label={t("billing.monthly")}
            />
            <SegButton
              ref={yearlyRef}
              active={period === "annual"}
              onClick={() => setPeriod("annual")}
              label={t("billing.annual")}
            />
          </div>
        </div>
      </header>

      {/* ── Plan cards ─────────────────────────────────────────────────── */}
      {/* Single column on mobile; ≥900px cards flex-wrap and pack side-by-side,
          dropping a card to the next row only when it can't fit (no shrink below 280px). */}
      {isLoading ? (
        <div className="flex justify-center py-20"><PageSpinner /></div>
      ) : isError ? (
        <p className="text-center py-20" style={{ color: "var(--text-tertiary)" }}>
          {t("checkout.errors.generic")}
        </p>
      ) : (
        // Centered flex row so the cards stay centered for any plan count
        // (backend may return 1–3 tiers); becomes a stacked column on mobile.
        <main className="flex flex-col items-center min-[900px]:flex-row min-[900px]:flex-wrap min-[900px]:items-start min-[900px]:justify-center gap-[18px] min-[900px]:gap-[22px] mx-auto max-w-[460px] min-[900px]:max-w-none">
          {groups.map((group, i) => (
            <PlanCard
              key={group.tier}
              group={group}
              period={period}
              numLocale={numLocale}
              delay={CARD_DELAYS[i] ?? "0s"}
              featured={group.tier === FEATURED_TIER}
              pending={pending}
              onSelect={(tier) => onSelectPlan(tier, period)}
            />
          ))}
          <EnterpriseCard />
        </main>
      )}
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────────────────── */

/** One billing-period tab. Forwarded ref lets the parent measure its box. */
const SegButton = ({
  ref,
  active,
  onClick,
  label,
}: {
  ref: React.Ref<HTMLButtonElement>;
  active: boolean;
  onClick: () => void;
  label: string;
}) => (
  <button
    ref={ref}
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className="relative z-[1] whitespace-nowrap cursor-pointer"
    style={{
      border: "none",
      background: "none",
      fontSize: 14,
      fontWeight: 600,
      color: active ? "var(--text-primary)" : "var(--text-tertiary)",
      padding: "9px 22px",
      borderRadius: "var(--radius-pill)",
      transition: "color .25s ease",
    }}
  >
    {label}
  </button>
);

function PlanCard({
  group,
  period,
  numLocale,
  delay,
  featured,
  pending,
  onSelect,
}: {
  group: TierGroup;
  period: BillingInterval;
  numLocale: string;
  delay: string;
  featured: boolean;
  pending: boolean;
  onSelect: (tier: ServiceTier) => void;
}) {
  const { t } = useTranslation("pricing");
  const tier = group.tier;
  // Prefer the selected interval; fall back so a card still renders if a tier is
  // missing one. Assumes every tier ships a monthly price (annual is shown /month).
  const plan = group.byInterval[period] ?? group.byInterval.monthly ?? group.byInterval.annual;
  // Every tier shares the same nine feature items; only the title limit differs.
  const features = t("plans.common.features", { returnObjects: true }) as string[];
  const titleLimit = t(`plans.${tier}.titleLimit`);

  // Annual price is the full-year amount; show it per-month, plus the annual total.
  const annualPlan = group.byInterval.annual;
  const displayMinor =
    period === "annual" && annualPlan?.unitAmount != null
      ? Math.round(annualPlan.unitAmount / 12)
      : (plan?.unitAmount ?? null);
  const priceText = formatPlanPrice(displayMinor, plan?.currency ?? null, numLocale);
  const annualTotalText = formatPlanPrice(annualPlan?.unitAmount ?? null, annualPlan?.currency ?? null, numLocale);

  // Real annual saving %, derived from the actual monthly vs annual amounts (not
  // a hard-coded figure). Digit glyphs follow numLocale's `-u-nu-` extension, so
  // they track the user's numberFormat preference rather than the UI language.
  const savingPct = annualSavingPercent(group);
  const savingPctText =
    savingPct == null ? null : new Intl.NumberFormat(numLocale).format(savingPct);

  return (
    <div
      className={cn(
        "w-full min-[900px]:w-auto min-[900px]:flex-[1_0_280px] min-[900px]:max-w-[360px]",
        featured && "min-[900px]:-translate-y-2",
      )}
    >
      <section
        className="relative flex flex-col overflow-hidden animate-fadeUp h-full"
        style={{
          background: "var(--surface-card)",
          border: `1px solid ${featured ? "var(--terracotta-border)" : "var(--border-light)"}`,
          borderRadius: 18,
          boxShadow: featured ? "var(--shadow-warm)" : undefined,
          animationDelay: delay,
        }}
      >
        {featured && (
          <span aria-hidden="true" className="absolute inset-x-0 top-0" style={{ height: 3, background: "linear-gradient(90deg, transparent, var(--terracotta), transparent)" }} />
        )}

        <div style={{ padding: "28px 26px 24px", borderBottom: "1px solid var(--divider)", background: featured ? "var(--terracotta-tint)" : undefined }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>{t(`plans.${tier}.name`)}</div>
              <div style={{ marginTop: 4, fontSize: 13.5, color: "var(--text-tertiary)" }}>{t(`plans.${tier}.tagline`)}</div>
            </div>
            {featured && (
              <span className="inline-flex items-center gap-1.5 shrink-0 uppercase pop-badge" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", padding: "6px 11px", borderRadius: "var(--radius-pill)" }}>
                <Star size={12} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                {t("popular")}
              </span>
            )}
          </div>

          <div className="flex items-baseline gap-2" style={{ marginTop: 22 }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 24, lineHeight: 1, letterSpacing: "-0.02em", color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>{priceText}</span>
            <span style={{ fontSize: 15, fontWeight: 500, color: "var(--text-tertiary)" }}>{t("perMonth")}</span>
          </div>

          <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--text-tertiary)", minHeight: 18 }}>
            {period === "annual" ? (
              <>
                {savingPctText != null && (
                  <>
                    <strong style={{ color: "var(--gold-text)", fontWeight: 700 }}>{t("billing.saveShort", { pct: savingPctText })}</strong>{" · "}
                  </>
                )}
                {t("billing.billedAnnually", { n: annualTotalText })}
              </>
            ) : (
              t("billing.billedMonthly")
            )}
          </div>
        </div>

        <ul className="list-none flex-1 flex flex-col" style={{ padding: "24px 26px 8px", gap: 15 }}>
          <li className="flex items-center gap-3 font-semibold" style={{ fontSize: 14, color: "var(--text-primary)" }}>
            <CircleCheckBig size={18} strokeWidth={2} aria-hidden="true" className="shrink-0" style={{ color: "var(--terracotta)" }} />
            {titleLimit}
          </li>
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-3" style={{ fontSize: 14, color: "var(--text-secondary)" }}>
              <CircleCheckBig size={18} strokeWidth={2} aria-hidden="true" className="shrink-0" style={{ color: "var(--terracotta)" }} />
              {feature}
            </li>
          ))}
        </ul>

        <div style={{ padding: "16px 26px 28px" }}>
          <ThemedButton
            variant="primary"
            size="full"
            disabled={pending || plan == null}
            data-testid="plan-cta"
            onClick={() => onSelect(tier)}
          >
            {t(`plans.${tier}.cta`)}
          </ThemedButton>
        </div>
      </section>
    </div>
  );
}

/**
 * EnterpriseCard — static "Custom quote" tier with no Stripe price.
 *
 * Mirrors PlanCard's chrome but swaps the numeric price for the localized
 * `priceLabel` and the checkout CTA for a `mailto:` contact anchor. Shows the
 * same nine shared features as the priced tiers, with the per-tier title limit
 * listed first. No `onSelect`/checkout flow — contact happens via email.
 */
function EnterpriseCard() {
  const { t } = useTranslation("pricing");
  const features = t("plans.common.features", { returnObjects: true }) as string[];
  const titleLimit = t("plans.enterprise.titleLimit");

  return (
    <div className="w-full min-[900px]:w-auto min-[900px]:flex-[1_0_280px] min-[900px]:max-w-[360px]">
      <section
        className="relative flex flex-col overflow-hidden animate-fadeUp h-full"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-light)",
          borderRadius: 18,
          animationDelay: "0.32s",
        }}
      >
        <div style={{ padding: "28px 26px 24px", borderBottom: "1px solid var(--divider)" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>{t("plans.enterprise.name")}</div>
            <div style={{ marginTop: 4, fontSize: 13.5, color: "var(--text-tertiary)" }}>{t("plans.enterprise.tagline")}</div>
          </div>

          <div className="flex items-baseline gap-2" style={{ marginTop: 22 }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 24, lineHeight: 1.2, letterSpacing: "-0.02em", color: "var(--text-primary)" }}>{t("plans.enterprise.priceLabel")}</span>
          </div>

          <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--text-tertiary)", minHeight: 18 }} />
        </div>

        <ul className="list-none flex-1 flex flex-col" style={{ padding: "24px 26px 8px", gap: 15 }}>
          <li className="flex items-center gap-3 font-semibold" style={{ fontSize: 14, color: "var(--text-primary)" }}>
            <CircleCheckBig size={18} strokeWidth={2} aria-hidden="true" className="shrink-0" style={{ color: "var(--terracotta)" }} />
            {titleLimit}
          </li>
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-3" style={{ fontSize: 14, color: "var(--text-secondary)" }}>
              <CircleCheckBig size={18} strokeWidth={2} aria-hidden="true" className="shrink-0" style={{ color: "var(--terracotta)" }} />
              {feature}
            </li>
          ))}
        </ul>

        <div style={{ padding: "16px 26px 28px" }}>
          <ThemedAnchorButton
            as="a"
            href={ENTERPRISE_CONTACT}
            variant="primary"
            size="full"
            data-testid="enterprise-cta"
          >
            {t("plans.enterprise.cta")}
          </ThemedAnchorButton>
        </div>
      </section>
    </div>
  );
}
